/**
 * The clock toggle (D153) — the player's switch for a game that runs a
 * real-time clock.
 *
 * @remarks
 * The third sibling of the Images and Sound toggles, and the one WCAG 2.2.1
 * has an opinion about: a time limit must be adjustable BEFORE the player
 * meets it, so this control renders on the title screen as well as in the
 * session, whenever the pack declares a clock. Turning it off stops silence
 * from costing a turn; it does not make the game easier — the turn budget is
 * the same either way (the arc doc's §5 line: reading at your own speed is
 * not choosing an easier game).
 *
 * A plain button carrying `aria-pressed`, naming its own state.
 *
 * @packageDocumentation
 */

/** Props for {@link ClockToggleComponent}. */
export interface ClockToggleComponentProps {
	/** Whether silence currently costs turns. */
	readonly isClockEnabled: boolean;
	/** Called with the state the player is asking for. */
	readonly onToggle: (isClockEnabled: boolean) => void;
}

/**
 * Render the clock on/off toggle.
 *
 * @param props - {@link ClockToggleComponentProps}
 * @returns The toggle button
 */
export const ClockToggleComponent = ({ isClockEnabled, onToggle }: ClockToggleComponentProps) => (
	<button
		aria-label={isClockEnabled ? 'Clock on — stop silence from costing turns' : 'Clock off — let silence cost turns'}
		aria-pressed={isClockEnabled}
		className="clock-toggle__trigger"
		onClick={() => {
			onToggle(!isClockEnabled);
		}}
		type="button"
	>
		<ClockIcon isEnabled={isClockEnabled} />
		<span className="clock-toggle__label">Clock</span>
	</button>
);

/**
 * The toggle's icon: a clock face, struck through when the clock is stopped
 * — the state is visible without reading, as the speaker's is.
 *
 * @param props - Whether the clock runs
 * @returns The icon
 */
const ClockIcon = ({ isEnabled }: { readonly isEnabled: boolean }) => (
	<svg
		aria-hidden="true"
		className="clock-toggle__icon"
		fill="none"
		focusable="false"
		height="16"
		stroke="currentColor"
		strokeLinecap="round"
		strokeLinejoin="round"
		strokeWidth={1.6}
		viewBox="0 0 24 24"
		width="16"
	>
		<circle cx="12" cy="12" r="8.5" />
		{isEnabled ? <path d="M12 7v5l3 2" /> : <path d="M4 20 20 4" />}
	</svg>
);
